import Skeleton from '@material-ui/lab/Skeleton';
import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Bar,
  BarChart,
  Legend,
  LegendPayload,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { track } from '~/src/dash/analytics/analytics';

import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import {
  EGroupByType,
  IDeliveryVariables,
  IInventoryParameters,
} from '~/src/dash/apiV2/forecasting/forecasting';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle, spacing } from '~/src/dash/components/layout/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  ICustomRenderCellJSXProps,
  IListViewTableColumnInfo,
  largeTableRowsPerPageOptions,
  RowsPerPage,
  smallTableRowsPerPageOptions,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  borderRadius,
  Col,
  Cols,
  fontSizing,
  IndependentSelect,
  Row,
  Rows,
  Span,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  RouteCampaignExisting,
  RouteCampaignSettingsList,
} from '~/src/dash/router/routes';
import { IListItem } from '~/src/dash/types/menuList';
import {
  addCommasToIntegerOrFloat,
  convertLargeNumbersToRoundedString,
} from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
} from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  formatDataToCSV,
  generateCSVColumns,
  IForecastingResponse,
} from '~/src/dash/views/tools/Forecasting/common';
import {
  forecastingDataConfig,
  formatGroupByDataToLVTRows,
  formatLineItemDataToLVTRows,
} from '~/src/dash/views/tools/Forecasting/Forecast/config';
import { CustomTooltip } from '~/src/dash/views/tools/Forecasting/Forecast/CustomTooltip';
import { ForecastCSVDownloadButton } from '~/src/dash/views/tools/Forecasting/Forecast/ForecastCSVDownloadButton';
import { ForecastingCard } from '~/src/dash/views/tools/Forecasting/Forecast/ForecastingCard';
import { SiteModal } from '~/src/dash/views/tools/Forecasting/Forecast/SiteModal';

interface IGroupByData {
  name: string;
  bookedImps: number;
  expectedImps: number;
  fullName?: string;
}

interface IForecastingDataProps {
  forecastingData: IForecastingResponse;
  totalRows: number;
  isActionPending: boolean;
  forecastingDataLoadState: LoadingState;
  setForecastingDataLoadState: (value: React.SetStateAction<number>) => void;
  inventoryParametersSnapshot: IInventoryParameters;
  deliveryVariables: IDeliveryVariables;
  deliveryVariablesSnapshot: IDeliveryVariables;
  handleRunForecast: (groupByType: EGroupByType) => void;
  groupByType: EGroupByType;
}

const groupByTypes = [
  {
    id: EGroupByType.Tier,
    label: 'Tier',
    isActive: true,
  },
  {
    id: EGroupByType.Site,
    label: 'Channel',
    isActive: false,
  },
] as ISelectOption[];

const fullLegendPayload = [
  {
    value: 'Expected Imps',
    type: 'circle',
    id: '1',
    color: publicaColors.lightTurquoise,
  },
  { value: 'Booked Imps', type: 'circle', id: '2', color: publicaColors.turquoise },
] as ReadonlyArray<LegendPayload>;

export const Forecast = ({
  forecastingData,
  forecastingDataLoadState,
  setForecastingDataLoadState,
  inventoryParametersSnapshot,
  deliveryVariables,
  deliveryVariablesSnapshot,
  handleRunForecast,
  groupByType,
}: IForecastingDataProps) => {
  const { isSuperUser, session, user, currencyMultiplier, currencySymbol } = useBaseStore();
  const { cypressClassName, tierTable, siteTable, lineItemTable } = forecastingDataConfig;

  const [isFreqCapActive, setIsFreqCapActive] = useState(deliveryVariables.FreqCapActive);
  const [breakdownCurrPage, setBreakdownCurrPage] = useState(tierTable.defaultState.page);
  const [lineItemCurrPage, setLineItemCurrPage] = useState(lineItemTable.defaultState.page);
  const [breakdownRowsPerPage, setBreakdownRowsPerPage] = useState<RowsPerPage>(
    tierTable.defaultState.rowsPerPage,
  );
  const [lineItemRowsPerPage, setLineItemRowsPerPage] = useState<RowsPerPage>(
    lineItemTable.defaultState.rowsPerPage,
  );
  const [enableBookedImps, setEnableBookedImps] = useState(true);
  const [legendPayload, setLegendPayload] =
    useState<ReadonlyArray<LegendPayload>>(fullLegendPayload);
  const [groupByTypeOptions, setGroupByTypeOptions] = useState(groupByTypes);
  const [selectedGroupByType, setSelectedGroupByType] = useState(
    groupByTypes.find((option) => option.isActive),
  );
  const [groupByData, setGroupByData] = useState<IGroupByData[]>([]);
  const [openSiteModal, setOpenSiteModal] = useState(false);
  const [siteModalData, setSiteModalData] = useState<ISmallSite[]>([]);
  const [siteModalLoadState, setSiteModalLoadState] = useState(LoadingStateLoading);

  const [breakdownOrderDirection, setBreakdownOrderDirection] = useState(
    tierTable.defaultState.orderDirection,
  );
  const [breakdownOrderBy, setBreakdownOrderBy] = useState(tierTable.defaultState.orderBy);
  const [lineItemOrderDirection, setLineItemOrderDirection] = useState(
    lineItemTable.defaultState.orderDirection,
  );
  const [lineItemOrderBy, setLineItemOrderBy] = useState(lineItemTable.defaultState.orderBy);

  const limitGroupByData = (data: IGroupByData[]) => {
    if (selectedGroupByType?.id === EGroupByType.Site) {
      return data.slice(
        breakdownCurrPage * breakdownRowsPerPage,
        (breakdownCurrPage + 1) * breakdownRowsPerPage,
      );
    }
    return data;
  };

  const handleApplySelectedGroupByType = (set: Set<string | number>) => {
    setForecastingDataLoadState(LoadingStateLoading);
    const newGroupByTypes = cloneDeep(groupByTypes);
    newGroupByTypes.forEach((type) => (type.isActive = false));
    let selectedID = EGroupByType.Tier;
    // set contains only 1 value, set the selectedID to be equal to that value
    set.forEach((val) => {
      selectedID = val as number;
    });
    newGroupByTypes.forEach((type) => {
      if (type.id === selectedID) {
        type.isActive = true;
      }
    });
    track('Forecast - View by Channel', { isSuperUser }, user.user);
    unstable_batchedUpdates(() => {
      setGroupByTypeOptions(newGroupByTypes);
      setSelectedGroupByType(newGroupByTypes.find((option) => option.isActive));
    });

    handleRunForecast(selectedID);
  };

  // update page number
  const handleChangePage = (page: number, table: string) => {
    if (table === 'breakdown') {
      setBreakdownCurrPage(page);
    } else {
      setLineItemCurrPage(page);
    }
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>, table: string) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    if (table === 'breakdown') {
      unstable_batchedUpdates(() => {
        setBreakdownRowsPerPage(updatedRowsPerPage);
        setBreakdownCurrPage(0);
      });
    } else {
      unstable_batchedUpdates(() => {
        setLineItemRowsPerPage(updatedRowsPerPage);
        setLineItemCurrPage(0);
      });
    }
  };

  const handleOpenSiteModal = (props: ICustomRenderCellJSXProps) => {
    unstable_batchedUpdates(() => {
      setSiteModalLoadState(LoadingStateLoading);
      setSiteModalData(props.value as unknown as ISmallSite[]);
      setSiteModalLoadState(LoadingStateHasData);
      setOpenSiteModal(true);
    });
  };

  const handleCloseSiteModal = () => {
    setOpenSiteModal(false);
  };

  const getLineItemRoute = (props: ICustomRenderCellJSXProps): string => {
    if (props.row.ID.value && props.row.OrderID.value) {
      return getRoutePath(RouteCampaignExisting, extractRouteParams({ session, user }), {
        c: props.row.ID.value as string,
        order: props.row.OrderID.value as string,
      });
    } else {
      return getRoutePath(RouteCampaignSettingsList, extractRouteParams({ session, user }));
    }
  };

  const convertToPublisherCurrencyValue = (value: string): string => {
    return `${currencySymbol}${addCommasToIntegerOrFloat(
      (parseFloat(value) * currencyMultiplier).toFixed(2),
    )}`;
  };

  const convertToPublisherCurrencyValueRounded = (value: string): string => {
    return `${currencySymbol}${addCommasToIntegerOrFloat(
      String(Math.round(parseFloat(value) * currencyMultiplier)),
    )}`;
  };

  // this sets up the changes to force a re-sort
  const handleOnClickBreakdownColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (breakdownOrderBy !== key) {
      unstable_batchedUpdates(() => {
        setBreakdownOrderDirection(defaultOrderDirection || 'asc');
        setBreakdownOrderBy(key);
        setBreakdownCurrPage(0);
      });
    } else {
      const isAsc = breakdownOrderBy === key && breakdownOrderDirection === 'asc';
      unstable_batchedUpdates(() => {
        setBreakdownOrderDirection(isAsc ? 'desc' : 'asc');
        setBreakdownOrderBy(key);
        setBreakdownCurrPage(0);
      });
    }
  };

  // this sets up the changes to force a re-sort
  const handleOnClickLineItemColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (lineItemOrderBy !== key) {
      unstable_batchedUpdates(() => {
        setLineItemOrderDirection(defaultOrderDirection || 'asc');
        setLineItemOrderBy(key);
        setLineItemCurrPage(0);
      });
    } else {
      const isAsc = lineItemOrderBy === key && lineItemOrderDirection === 'asc';
      unstable_batchedUpdates(() => {
        setLineItemOrderDirection(isAsc ? 'desc' : 'asc');
        setLineItemOrderBy(key);
        setLineItemCurrPage(0);
      });
    }
  };

  useEffect(() => {
    if (enableBookedImps) {
      setLegendPayload(fullLegendPayload);
    } else {
      setLegendPayload([
        {
          value: 'Expected Imps',
          type: 'circle',
          id: '1',
          color: publicaColors.lightTurquoise,
        },
      ]);
    }
  }, [enableBookedImps]);

  useEffect(() => {
    setIsFreqCapActive(deliveryVariables.FreqCapActive);
  }, [deliveryVariables.FreqCapActive]);

  // rerenders top part of graph
  useEffect(() => {
    const newGroupByTypes = cloneDeep(groupByTypes);
    if (groupByType === EGroupByType.Site) {
      newGroupByTypes[0].isActive = false;
      newGroupByTypes[1].isActive = true;
    }
    unstable_batchedUpdates(() => {
      setGroupByTypeOptions(newGroupByTypes);
      setSelectedGroupByType(newGroupByTypes.find((option) => option.isActive));
      setEnableBookedImps(groupByType !== EGroupByType.Site);
    });
  }, [groupByType]);

  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      if (selectedGroupByType?.id === EGroupByType.Site) {
        const newGroupByData = forecastingData.ForecastingData.map((obj) => {
          return {
            name: obj.SiteName,
            bookedImps: obj.BookedImps,
            expectedImps: obj.ExpectedImps,
            fullName: obj.SiteName,
          };
        });
        unstable_batchedUpdates(() => {
          setGroupByData(newGroupByData);
          setBreakdownCurrPage(siteTable.defaultState.page);
          setBreakdownRowsPerPage(siteTable.defaultState.rowsPerPage);
        });
      } else {
        const newGroupByData = forecastingData.ForecastingData.map((obj) => {
          return {
            name: obj.Tier === 'All' ? obj.Tier : `Tier ${obj.Tier}`,
            bookedImps: obj.BookedImps,
            expectedImps: obj.ExpectedImps,
            fullName: obj.Tier === 'All' ? obj.Tier : `Tier ${obj.Tier}`,
          };
        });
        unstable_batchedUpdates(() => {
          setGroupByData(newGroupByData);
          setBreakdownCurrPage(tierTable.defaultState.page);
          setBreakdownRowsPerPage(tierTable.defaultState.rowsPerPage);
        });
      }

      unstable_batchedUpdates(() => {
        setLineItemCurrPage(lineItemTable.defaultState.page);
        setLineItemRowsPerPage(lineItemTable.defaultState.rowsPerPage);
      });
    } else {
      isMounted.current = true;
    }
  }, [forecastingData, selectedGroupByType]);

  const determineXAxisParam = (): string => {
    let param = 'expectedImps';
    if (!enableBookedImps) {
      return param;
    }
    let highestValue = 0;
    for (const data of groupByData) {
      if (data.bookedImps > highestValue) {
        param = 'bookedImps';
        highestValue = data.bookedImps;
      } else if (data.expectedImps > highestValue) {
        param = 'expectedImps';
        highestValue = data.expectedImps;
      }
    }
    return param;
  };

  // reset on unmount; in React 18, components can mount again
  // double call is necessary
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  return (
    <Rows basePadding gap={spacing.base} style={{ ...highlightedDivStyle, minWidth: '940px' }}>
      <Row noPadding>
        <SiteModal
          openSiteModal={openSiteModal}
          handleCloseSiteModal={handleCloseSiteModal}
          sites={siteModalData}
          loadState={siteModalLoadState}
        />
      </Row>
      {/* TITLE */}
      <Row noPadding>
        <Cols
          style={{
            justifyContent: 'space-between',
          }}
        >
          <Col>
            <Cols
              gap={spacing.extraLarge}
              style={{
                alignItems: 'center',
              }}
            >
              <Col>
                <Cols
                  gap={spacing.small}
                  style={{
                    alignItems: 'center',
                  }}
                >
                  <Col displayFlex>
                    <IconV2 name='shake' color={publicaColors.black} />
                  </Col>
                  <Col>
                    <Text bold large color={publicaColors.black}>
                      Forecast
                    </Text>
                  </Col>
                </Cols>
              </Col>
              <Col>
                {forecastingData.GeneratedAt && (
                  <Cols>
                    <Col>
                      <Text color={publicaColors.darkGray} extraSmall>
                        Generated on: &nbsp;
                      </Text>
                    </Col>
                    <Col>
                      <Text extraSmall>{`[${moment(forecastingData.GeneratedAt)
                        .tz(session.publisher.Timezone)
                        .format('MMM DD, YYYY - hh:mm A')}]`}</Text>
                    </Col>
                  </Cols>
                )}
              </Col>
            </Cols>
          </Col>
          <Col>
            <Cols
              gap={spacing.base}
              style={{
                alignItems: 'center',
              }}
            >
              <Col>
                <Cols
                  gap={spacing.extraSmall}
                  style={{
                    alignItems: 'center',
                    border:
                      forecastingDataLoadState !== LoadingStateHasData && !isEmpty(forecastingData)
                        ? `0.5px solid ${publicaColors.gray}`
                        : `0.5px solid ${publicaColors.blue}`,
                    borderRadius: borderRadius.small,
                    padding: `${spacing.extraSmall} ${spacing.small}`,
                    cursor:
                      forecastingDataLoadState !== LoadingStateHasData && !isEmpty(forecastingData)
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  <Col displayFlex>
                    <IconV2
                      name='download'
                      color={
                        forecastingDataLoadState !== LoadingStateHasData &&
                        !isEmpty(forecastingData)
                          ? publicaColors.gray
                          : publicaColors.blue
                      }
                    />
                  </Col>
                  <Col
                    onClick={() => {
                      track('Forecast - Download CSV', { isSuperUser }, user.user);
                    }}
                  >
                    <ForecastCSVDownloadButton
                      isDisabled={
                        forecastingDataLoadState !== LoadingStateHasData &&
                        !isEmpty(forecastingData)
                      }
                      columns={generateCSVColumns(enableBookedImps)}
                      datas={() =>
                        formatDataToCSV(
                          generateCSVColumns(enableBookedImps),
                          session.publisher.ID,
                          forecastingData,
                          inventoryParametersSnapshot,
                          deliveryVariablesSnapshot,
                          enableBookedImps,
                          currencySymbol,
                          currencyMultiplier,
                          groupByType,
                        )
                      }
                      filename='Forecasting Overview'
                      title=''
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Row>
      {!isFreqCapActive && (
        <Row
          style={{
            backgroundColor: publicaColors.lighterBlue,
            width: 'fit-content',
            borderRadius: borderRadius.small,
            padding: '2px 4px',
          }}
        >
          <Text small color={publicaColors.black} style={{ display: 'flex', alignItems: 'center' }}>
            We estimate you'll fill&nbsp;
            {forecastingDataLoadState === LoadingStateLoading ? (
              <Skeleton variant='text' animation='wave' width={40} height={28} />
            ) : (
              <Span heavy>{`${addCommasToIntegerOrFloat(
                String(forecastingData?.ForecastingCards?.TotalExpectedImps || '0'),
              )} impressions`}</Span>
            )}
            &nbsp;over the forecasted dates.
          </Text>
        </Row>
      )}
      {/* CARDS */}
      <Row noPadding>
        <Rows gap={spacing.small}>
          <Row noPadding>
            <Cols gap={spacing.base}>
              <Col>
                <ForecastingCard
                  icon='columns'
                  name='Total Pods'
                  value={forecastingData?.ForecastingCards?.TotalPods || 0}
                  tooltip='Total ad break opportunities based on historical endpoint request counts.'
                  loadState={forecastingDataLoadState}
                />
              </Col>
              <Col>
                <ForecastingCard
                  icon='activity'
                  name='Renderable Pods'
                  value={forecastingData?.ForecastingCards?.RenderablePods || 0}
                  tooltip='Total ad break opportunities based on historical endpoint request counts, adjusted for the average render rate of forecasted inventory.'
                  loadState={forecastingDataLoadState}
                />
              </Col>
            </Cols>
          </Row>
          {!isFreqCapActive && (
            <Row noPadding>
              <Cols gap={spacing.base}>
                <Col>
                  <Cols gap={spacing.base}>
                    <Col>
                      <ForecastingCard
                        icon='circle-play'
                        name='Total Available Slots'
                        value={forecastingData?.ForecastingCards?.TotalSlots || 0}
                        tooltip='Total available impression opportunities based on historic data.'
                        loadState={forecastingDataLoadState}
                      />
                    </Col>
                    <Col>
                      <ForecastingCard
                        icon='activity'
                        name='Renderable Slots'
                        value={forecastingData?.ForecastingCards?.RenderableAvails || 0}
                        tooltip='Total available impression opportunities based on historical data, adjusted for the average render rate of forecasted inventory.'
                        loadState={forecastingDataLoadState}
                      />
                    </Col>
                    <Col>
                      <ForecastingCard
                        icon='channels'
                        name='Expected Imps'
                        value={forecastingData?.ForecastingCards?.TotalExpectedImps || 0}
                        tooltip='Estimated impressions based on historical trends, adjusted for render rate.'
                        loadState={forecastingDataLoadState}
                      />
                    </Col>
                    {groupByType === EGroupByType.Tier && enableBookedImps && (
                      <Col>
                        <ForecastingCard
                          icon='flag'
                          name='Booked Imps'
                          value={forecastingData?.ForecastingCards?.TotalBookedImps || 0}
                          tooltip='Maximum impressions expected to serve from Direct Campaigns in Publica during the forecasted period.'
                          loadState={forecastingDataLoadState}
                        />
                      </Col>
                    )}
                    <Col>
                      <ForecastingCard
                        icon='activity'
                        name='Avg. Slots/Pod'
                        value={forecastingData?.ForecastingCards?.AvgSlotsPerPod || 0}
                        tooltip='Average number of renderable slots available per pod.'
                        loadState={forecastingDataLoadState}
                      />
                    </Col>
                    <Col>
                      <ForecastingCard
                        icon='activity'
                        name='Avg. Slot Length'
                        value={forecastingData?.ForecastingCards?.AvgSlotLength || 0}
                        tooltip='Average length of impressions.'
                        loadState={forecastingDataLoadState}
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
          )}
        </Rows>
      </Row>
      {/* GRAPH */}
      {!isFreqCapActive && (
        <Row noPadding>
          <Rows
            verticalPadding
            style={{ ...highlightedDivStyle, minWidth: '940px' }}
            gap={spacing.base}
          >
            <Row noVerticalPadding>
              <Cols gap='44px'>
                <Col>
                  <Text heavy color={publicaColors.black}>
                    Breakdown
                  </Text>
                </Col>
                <Col>
                  <Cols
                    gap={spacing.small}
                    style={{
                      alignItems: 'center',
                    }}
                  >
                    <Col displayFlex>
                      <IconV2 name='customize' color={publicaColors.black} />
                    </Col>
                    <Col>
                      <Text bold small color={publicaColors.black}>
                        View by
                      </Text>
                    </Col>
                    {forecastingDataLoadState === LoadingStateLoading ? (
                      <Col>
                        <Skeleton variant='text' animation='wave' width={80} height={28} />
                      </Col>
                    ) : (
                      <Col>
                        <IndependentSelect
                          key='groupByType'
                          placeholder=''
                          listLabel=''
                          selectedLimit={1}
                          options={groupByTypeOptions}
                          selectedOptionIDs={
                            selectedGroupByType
                              ? new Set([selectedGroupByType.id])
                              : new Set([groupByTypes[0].id])
                          }
                          singularLabel='groupBy type'
                          handleApplySelect={handleApplySelectedGroupByType}
                          minWidth={60}
                          popoverWidth={152}
                          height={28}
                          isSelectionRequired={true}
                          disableSearch={true}
                          isCheckboxHidden={true}
                        />
                      </Col>
                    )}
                  </Cols>
                </Col>
                {groupByType === EGroupByType.Tier &&
                  (forecastingDataLoadState === LoadingStateLoading ? (
                    <Col>
                      <Skeleton variant='text' animation='wave' width={100} height={28} />
                    </Col>
                  ) : (
                    <Col>
                      <TextButton
                        size='extraSmall'
                        weight='bold'
                        style={{
                          display: 'flex',
                          gap: spacing.small,
                          color: enableBookedImps ? publicaColors.blue : publicaColors.gray,
                          border: enableBookedImps
                            ? `0.5px solid ${publicaColors.blue}`
                            : `0.5px solid ${publicaColors.gray}`,
                          borderRadius: borderRadius.small,
                          padding: spacing.extraSmall,
                        }}
                        onClick={() => {
                          track('Forecast - Hide Booked Imps', { isSuperUser }, user.user);
                          setEnableBookedImps(!enableBookedImps);
                        }}
                      >
                        <IconV2
                          name='eye'
                          color={enableBookedImps ? publicaColors.blue : publicaColors.gray}
                        />
                        Booked Imps.
                      </TextButton>
                    </Col>
                  ))}
              </Cols>
            </Row>
            <Row noPadding>
              {forecastingDataLoadState === LoadingStateLoading && (
                <Skeleton variant='rect' animation='wave' width='100%'>
                  <ResponsiveContainer width={800} height={400}>
                    <BarChart layout='vertical' data={[]}></BarChart>
                  </ResponsiveContainer>
                </Skeleton>
              )}
              {forecastingDataLoadState !== LoadingStateLoading && isEmpty(groupByData) && (
                <Rows
                  style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Row
                    noPadding
                    displayFlex
                    height={400}
                    style={{
                      alignItems: 'center',
                    }}
                  >
                    No data to display configure forecasting above
                  </Row>
                </Rows>
              )}
              {forecastingDataLoadState !== LoadingStateLoading && !isEmpty(groupByData) && (
                <ResponsiveContainer width={800} height={400}>
                  <BarChart layout='vertical' data={limitGroupByData(groupByData)}>
                    <Legend
                      verticalAlign='top'
                      align='left'
                      iconType='circle'
                      payload={legendPayload}
                      wrapperStyle={{ marginLeft: 10 }}
                    />
                    <XAxis
                      dataKey={determineXAxisParam()}
                      type='number'
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(tick) => {
                        return convertLargeNumbersToRoundedString(tick);
                      }}
                      padding={{
                        right: 20,
                        left: 40,
                      }}
                      tick={{ fontSize: fontSizing.small }}
                    />
                    <YAxis
                      dataKey='name'
                      type='category'
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value: string) => value.substring(0, 30)}
                      padding={{
                        top: 20,
                        bottom: 20,
                      }}
                      tick={{ fontSize: fontSizing.small }}
                      yAxisId={0}
                      width={selectedGroupByType?.id === EGroupByType.Site ? 240 : 80}
                    />
                    {/* adds hover */}
                    <Tooltip
                      label='fullName'
                      content={<CustomTooltip />}
                      cursor={{
                        fill: 'transparent',
                      }}
                    />
                    <Bar
                      dataKey='expectedImps'
                      name='Expected Imps'
                      fill={publicaColors.lightTurquoise}
                      radius={30}
                      legendType='circle'
                      background={false}
                      barSize={16}
                      yAxisId={0}
                    />
                    <Bar
                      dataKey='bookedImps'
                      name='Booked Imps'
                      fill={publicaColors.turquoise}
                      radius={30}
                      legendType='circle'
                      background={false}
                      barSize={16}
                      hide={!enableBookedImps}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Row>
            <Row noVerticalPadding>
              {selectedGroupByType?.id === EGroupByType.Tier && (
                <ListViewTable
                  customFunctions={{
                    handleOpenSiteModal,
                    convertToPublisherCurrencyValue,
                    convertToPublisherCurrencyValueRounded,
                  }}
                  activeColumnKeys={Object.values(
                    extractVisibleColumns(
                      forecastingDataConfig.tierTable.columns,
                      enableBookedImps
                        ? tierTable.defaultState.tierActiveColumnKeys
                        : tierTable.defaultState.tierActiveColumnKeysWithoutBookedImps,
                      isSuperUser,
                    ),
                  )
                    .filter((column: any) => column.selected)
                    .map((column: any) => column.ID)}
                  columns={forecastingDataConfig.tierTable.columns}
                  handleChangePage={(_e, newPage) => handleChangePage(newPage, 'breakdown')}
                  handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(e, 'breakdown')}
                  handleOnClickColumnHeader={handleOnClickBreakdownColumnHeader}
                  isReadAuthorized
                  isSuperUser={isSuperUser}
                  isWriteAuthorized
                  loadState={forecastingDataLoadState}
                  order={breakdownOrderDirection}
                  orderBy={breakdownOrderBy}
                  page={breakdownCurrPage}
                  rows={formatGroupByDataToLVTRows(
                    forecastingData.ForecastingData,
                    forecastingData.ForecastingDataSummed,
                    forecastingDataConfig.tierTable,
                    breakdownCurrPage,
                    breakdownRowsPerPage,
                    breakdownOrderDirection,
                    breakdownOrderBy,
                  )}
                  rowsPerPage={breakdownRowsPerPage}
                  totalRowCount={
                    !isEmpty(forecastingData.ForecastingData)
                      ? forecastingData.ForecastingData.length + 1
                      : 0
                  }
                  rowsPerPageOptions={smallTableRowsPerPageOptions}
                  className={cypressClassName}
                />
              )}
              {selectedGroupByType?.id === EGroupByType.Site && (
                <ListViewTable
                  customFunctions={{
                    convertToPublisherCurrencyValue,
                    convertToPublisherCurrencyValueRounded,
                  }}
                  activeColumnKeys={Object.values(
                    extractVisibleColumns(
                      siteTable.columns,
                      siteTable.defaultState.siteActiveColumnKeys,
                      isSuperUser,
                    ),
                  )
                    .filter((column: any) => column.selected)
                    .map((column: any) => column.ID)}
                  columns={forecastingDataConfig.siteTable.columns}
                  handleChangePage={(_e, newPage) => handleChangePage(newPage, 'breakdown')}
                  handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(e, 'breakdown')}
                  handleOnClickColumnHeader={handleOnClickBreakdownColumnHeader}
                  isReadAuthorized
                  isSuperUser={isSuperUser}
                  isWriteAuthorized
                  loadState={forecastingDataLoadState}
                  order={breakdownOrderDirection}
                  orderBy={breakdownOrderBy}
                  page={breakdownCurrPage}
                  rows={formatGroupByDataToLVTRows(
                    forecastingData.ForecastingData,
                    forecastingData.ForecastingDataSummed,
                    forecastingDataConfig.siteTable,
                    breakdownCurrPage,
                    breakdownRowsPerPage,
                    breakdownOrderDirection,
                    breakdownOrderBy,
                  )}
                  rowsPerPage={breakdownRowsPerPage}
                  totalRowCount={forecastingData.ForecastingData.length} // change back to totalRows
                  rowsPerPageOptions={smallTableRowsPerPageOptions}
                  className={cypressClassName}
                />
              )}
            </Row>
          </Rows>
        </Row>
      )}
      {/* LINE ITEMS TABLE */}
      {groupByType === EGroupByType.Tier && (
        <Row noPadding>
          <Rows
            basePadding
            style={{ ...highlightedDivStyle, minWidth: '940px' }}
            gap={spacing.small}
          >
            <Row noPadding>
              <Text heavy>Overlapping line items</Text>
            </Row>
            <Row noPadding>
              <Text small>
                The following line items match inventory targeting over the forecasted period.
                Prioritize new campaigns accordingly.
              </Text>
            </Row>
            <Row noPadding>
              <ListViewTable
                customFunctions={{
                  getLineItemRoute,
                  convertToPublisherCurrencyValue,
                }}
                activeColumnKeys={Object.values(
                  extractVisibleColumns(
                    forecastingDataConfig.lineItemTable.columns,
                    lineItemTable.defaultState.lineItemActiveColumnKeys || [],
                    isSuperUser,
                  ),
                )
                  .filter((column: IListItem) => column.selected)
                  .map((column: IListItem) => column.ID)}
                columns={forecastingDataConfig.lineItemTable.columns}
                handleChangePage={(_e, newPage) => handleChangePage(newPage, 'lineItem')}
                handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(e, 'lineItem')}
                handleOnClickColumnHeader={handleOnClickLineItemColumnHeader}
                isReadAuthorized
                isSuperUser={isSuperUser}
                isWriteAuthorized
                loadState={forecastingDataLoadState}
                order={lineItemOrderDirection}
                orderBy={lineItemOrderBy}
                page={lineItemCurrPage}
                rows={formatLineItemDataToLVTRows(
                  forecastingData.OverlappingLineItems || [],
                  forecastingDataConfig.lineItemTable,
                  lineItemCurrPage,
                  lineItemRowsPerPage,
                  lineItemOrderDirection,
                  lineItemOrderBy,
                )}
                rowsPerPage={lineItemRowsPerPage}
                totalRowCount={forecastingData?.OverlappingLineItems?.length || 0}
                rowsPerPageOptions={largeTableRowsPerPageOptions}
                className={cypressClassName}
              />
            </Row>
          </Rows>
        </Row>
      )}
    </Rows>
  );
};
